import { Flex, type FlexProps, Grid, Icon, Image, Text } from "@chakra-ui/react";
import { DefaultNFTIcon, DefaultTokenIcon } from "@icons";
import { useColor } from "@styles/useColor";
import {
  OperationTypes,
  SafeSendAssetType,
  type Transaction,
  TransactionDirections,
  formatCurrency,
} from "@umami/chains";
import { useGetAssetPrice, useGetNetworkChain } from "@umami/state";
import { useMemo } from "react";

import { TransactionDirectionDetails } from "./TransactionDirectionDetails";
import { getTransactionAmount } from "./utils";

import { ChainIcon } from "@/components/Token/ChainIcon";

type TransactionTileProps = FlexProps & {
  operation: Transaction;
};

export const TransactionTile = ({ operation, ...props }: TransactionTileProps) => {
  const color = useColor();
  const getAssetPrice = useGetAssetPrice();
  const getNetworkChain = useGetNetworkChain();

  const isSendTransaction = operation.txDirection === TransactionDirections.SEND;

  const TransactionIcon = useMemo(() => {
    const { operationType, iconUrl, isNft, ticker } = operation;

    switch (operationType) {
      case OperationTypes.TRANSACTION:
      case OperationTypes.SHIELD:
      case OperationTypes.UNSHIELD:
      case OperationTypes.SAPLING:
        return <ChainIcon boxSize="48px" chainId={getNetworkChain(operation.chainId)} />;
      case OperationTypes.TOKEN_TRANSFER:
      case OperationTypes.TOKEN_BURNING:
      case OperationTypes.TOKEN_MINTING: {
        const rounded = isNft ? "md" : "full";
        const DefaultIcon = isNft ? DefaultNFTIcon : DefaultTokenIcon;

        return iconUrl ? (
          <Image boxSize="48px" alt={`${ticker || "Token"} icon`} rounded={rounded} src={iconUrl} />
        ) : (
          <Icon
            as={DefaultIcon}
            boxSize="48px"
            data-testid={isNft ? "default-nft-icon" : "default-token-icon"}
            rounded={rounded}
          />
        );
      }
      case OperationTypes.CONTRACT_CALL: {
        return <Icon boxSize="48px" rounded="full" />;
      }
      case OperationTypes.SAFE_SEND: {
        const { assetType } = operation;

        if (assetType === SafeSendAssetType.TEZ) {
          return <ChainIcon boxSize="48px" chainId={getNetworkChain(operation.chainId)} />;
        }

        return <Icon as={DefaultTokenIcon} boxSize="48px" rounded="full" />;
      }
    }
  }, [operation, getNetworkChain]);

  const getUsdPrice = () => {
    if (operation.isNft) {
      return `x${operation.amount}`;
    }

    const asset = operation.token ?? operation.ticker;

    return formatCurrency(getAssetPrice(asset) * Number(operation.amount));
  };

  return (
    <Grid
      alignItems="stretch"
      justifyContent="space-between"
      gridTemplateRows={{ base: "repeat(3, auto)", md: "repeat(2, auto)" }}
      gridTemplateColumns={{ base: "auto auto 1fr", md: "auto auto 1fr auto" }}
      gridTemplateAreas={{
        base: "'safe-send safe-send safe-send' 'icon title amount' 'icon details details'",
        md: "'icon title safe-send amount' 'icon details details amount'",
      }}
      width="100%"
      paddingBottom="30px"
      borderBottom="1px solid"
      borderBottomColor={color("100")}
      _first={{ paddingTop: 0 }}
      _last={{ paddingBottom: 0, borderBottom: "none" }}
      _only={{ paddingBottom: 0, borderBottom: "none" }}
      data-testid="operation-tile-transaction"
      paddingY="30px"
      {...props}
    >
      <Flex
        justifyContent={{ base: "center", md: "flex-start" }}
        alignSelf="center"
        gridArea="icon"
        marginRight="12px"
      >
        {TransactionIcon}
      </Flex>
      <TransactionDirectionDetails operation={operation} />
      <Flex
        alignItems="flex-end"
        justifyContent={{ base: "flex-end", md: "space-around" }}
        flexDirection={{ base: "row", md: "column" }}
        alignSelf={{ base: "center", md: "normal" }}
        gap="4px"
        gridArea="amount"
        height="auto"
      >
        <Text
          color={isSendTransaction ? color("900") : color("green")}
          fontSize={{ base: "sm", md: "md" }}
          fontWeight={600}
          data-testid="amount"
        >
          {operation.isNft
            ? operation.ticker
            : getTransactionAmount(operation, operation.amount.toString()) + " " + operation.ticker}
        </Text>
        <Text color={color("600")} fontSize="sm">
          {getUsdPrice()}
        </Text>
      </Flex>
    </Grid>
  );
};
